import {
    Action,
    IAgentRuntime,
    Memory,
    State,
    HandlerCallback,
    elizaLogger,
} from "@elizaos/core";
import { initCotiWalletProvider } from "../providers/wallet";
import { WalletInfoParams, WalletInfoSchema } from "../types/index";
import { ethers } from "ethers";

export const walletInfoAction: Action = {
    name: "WALLET_INFO",
    similes: [
        "GET_WALLET_INFO",
        "WALLET_DETAILS",
        "WALLET_STATUS",
        "ACCOUNT_INFO",
        "WALLET_SUMMARY"
    ],
    description: "Get detailed information about the COTI wallet including address, balance, and network details",
    validate: async (runtime: IAgentRuntime, message: Memory): Promise<boolean> => {
        try {
            // This action is always valid if we can initialize the wallet provider
            const walletProvider = initCotiWalletProvider(runtime);
            return walletProvider.getAddress() !== "";
        } catch {
            return false;
        }
    },
    handler: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State | undefined,
        options: any = {},
        callback?: HandlerCallback
    ) => {
        try {
            elizaLogger.info("Starting WALLET_INFO action");

            // Parse options if provided
            let includeBalance = true;
            if (message.content?.includeBalance !== undefined) {
                const params = WalletInfoSchema.parse(message.content);
                includeBalance = params.includeBalance;
            }

            const walletProvider = initCotiWalletProvider(runtime);
            const address = walletProvider.getAddress();
            const provider = walletProvider.getProvider();
            
            // Get network information
            const network = await provider.getNetwork();
            const blockNumber = await provider.getBlockNumber();
            const gasPrice = await provider.getGasPrice();

            let walletInfo: any = {
                address,
                network: {
                    name: network.name,
                    chainId: network.chainId.toString(),
                    blockNumber,
                    gasPrice: ethers.utils.formatUnits(gasPrice, "gwei"),
                },
            };

            let responseText = `COTI Wallet Information:
Address: ${address}
Network: ${network.name} (Chain ID: ${network.chainId})
Current Block: ${blockNumber}
Gas Price: ${ethers.utils.formatUnits(gasPrice, "gwei")} gwei`;

            // Include balance if requested
            if (includeBalance) {
                const balance = await walletProvider.getBalance();
                const transactionCount = await provider.getTransactionCount(address);
                
                walletInfo.balance = ethers.utils.formatEther(balance);
                walletInfo.balanceWei = balance.toString();
                walletInfo.transactionCount = transactionCount;

                responseText += `
Balance: ${ethers.utils.formatEther(balance)} COTI
Transaction Count: ${transactionCount}`;
            }

            if (callback) {
                await callback({
                    text: responseText,
                    content: {
                        success: true,
                        walletInfo,
                    },
                });
            }

            return {
                text: responseText,
                success: true,
                data: {
                    walletInfo,
                },
            };
        } catch (error) {
            elizaLogger.error("Error in WALLET_INFO action:", error);
            const errorMessage = `Failed to get wallet information: ${error instanceof Error ? error.message : String(error)}`;

            if (callback) {
                await callback({
                    text: errorMessage,
                    content: { success: false, error: errorMessage },
                });
            }

            return {
                text: errorMessage,
                success: false,
                error: error instanceof Error ? error : new Error(String(error)),
            };
        }
    },
    examples: [
        [
            {
                name: "{{user1}}",
                content: {
                    text: "Show me my wallet information",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "I'll get your COTI wallet details for you.",
                    action: "WALLET_INFO",
                },
            },
        ],
        [
            {
                name: "{{user1}}",
                content: {
                    text: "What's my wallet status?",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "Let me check your COTI wallet status and information.",
                    action: "WALLET_INFO",
                },
            },
        ],
    ],
};
